import { api } from './api';

export interface PublicUser {
  id: string;
  email: string | null;
  name: string | null;
  pictureUrl: string | null;
  countryCode: string | null;
  primaryLocale: string | null;
  emailVerified: boolean;
  /**
   * The tenant the user belongs to. `null` for platform staff who don't
   * belong to any particular company. Used by company-scoped picker UIs.
   */
  companyId?: string | null;
  /**
   * Global PatronEZ role (from `users.role`). `'owner' | 'admin'` unlocks the
   * /admin section; `'member'` is the default. Distinct from workspace-level
   * role on `company_members`.
   */
  role?: 'owner' | 'admin' | 'member';
}

export const authApi = {
  /** Returns the current user when the session cookie is valid, or null. */
  async me(): Promise<PublicUser | null> {
    try {
      const r = await api.get<PublicUser>('/api/auth/me');
      return r.data;
    } catch {
      return null;
    }
  },

  async updateProfile(input: { name?: string; pictureUrl?: string }): Promise<PublicUser> {
    const r = await api.patch<PublicUser>('/api/auth/me', input);
    return r.data;
  },

  async uploadAvatar(file: Blob): Promise<PublicUser> {
    const fd = new FormData();
    fd.append('file', file, 'avatar.png');
    const r = await api.post<PublicUser>('/api/auth/me/avatar', fd);
    return r.data;
  },

  async logout(): Promise<void> {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Ignore — cookie is HttpOnly, the user is already effectively logged out client-side.
    }
  },

  /** Returns the absolute URL the browser should navigate to for Google OAuth. */
  googleStartUrl(): string {
    const base = (import.meta.env.VITE_API_URL as string | undefined) || '';
    return `${base}/auth/google`;
  },
};
